import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChild,
  type Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideBluetooth, lucideTrash2, lucideUnlink, lucideUsb, lucideX } from '@ng-icons/lucide';
import { HlmBadge } from '@spartan-ng/helm/badge';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmSheet, HlmSheetImports } from '@spartan-ng/helm/sheet';
import { finalize, timeout } from 'rxjs';
import Swal from 'sweetalert2';

import { closeLoader, dispararLoader } from 'src/app/helpers/swall.helpers';
import {
  NiimbotPrinterService,
  type NiimbotConnectionStatus,
} from 'src/app/services/niimbot-printer/niimbot-printer.service';

/**
 * Chave de sessão do "o usuário já pediu impressora nesta sessão".
 *
 * A reconexão automática do Niimbot abre um prompt do navegador; disparar isso
 * para quem nunca clicou em nada seria um pop-up do nada ao abrir a tela. A
 * chave fica em `sessionStorage` (e não `localStorage`) de propósito: o
 * consentimento vale para a aba/sessão, não para sempre. Mesmo nome da tela
 * antiga, para quem já estava com a impressora ligada não perder o estado.
 */
const CHAVE_BOTAO_CLICADO = 'niimbot-button-clicked';

/** Buffer de logs: o serviço é falante, e a lista é só diagnóstico. */
const MAX_LOGS = 50;

/** Espera antes do auto-reconnect: dá tempo do overlay/portal assentar. */
const ATRASO_RECONEXAO_MS = 1000;

/**
 * Prazo da desconexão.
 *
 * A lib do Niimbot vem de CDN e fala Web Bluetooth/Serial: com o transporte já
 * caído, a promise de `disconnect()` pode nunca resolver — e sem `next` nem
 * `error` o `finalize` também não roda, deixando o loader preso na tela.
 *
 * Só na desconexão: `connect()` abre o seletor de dispositivo do navegador e
 * fica parado enquanto a pessoa procura e liga a impressora, o que passa fácil
 * de qualquer prazo razoável. Lá o desfecho vem do próprio prompt (escolher ou
 * cancelar), e o `finalize` basta.
 */
const PRAZO_DESCONEXAO_MS = 15_000;

/**
 * Gerenciador da impressora térmica (Niimbot) da tela de Check-ins.
 *
 * Bottom sheet no mobile, diálogo no desktop — mesmo conteúdo nos dois
 * (`#corpo`), porque na tela antiga o modal só existia em uma forma e ficava
 * espremido no celular, que é justamente onde a impressora é usada.
 *
 * O componente NÃO possui a conexão: quem possui é o `NiimbotPrinterService`
 * (`providedIn: 'root'`), então a impressora sobrevive a abrir/fechar o
 * gerenciador e à navegação entre telas. Aqui só há a UI e o signal público
 * `conectada`, que o pai lê para pintar o ícone de impressora no cabeçalho.
 *
 * ```html
 * <app-checkin-impressora #impressora />
 * <button (click)="impressora.abrir()">Impressora</button>
 * ```
 */
@Component({
  selector: 'app-checkin-impressora',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, NgIcon, HlmBadge, HlmButton, ...HlmSheetImports],
  providers: [provideIcons({ lucideBluetooth, lucideUsb, lucideUnlink, lucideTrash2, lucideX })],
  template: `
    <!--
      Um sheet só: bottom no mobile, RIGHT no desktop (era dialog centralizado,
      que em tela baixa cortava a lista de logs). Lado por ternário único — duas
      classes/valores lado a lado quebram com a inversão de camada do !important.

      admin-portal-scope repetido no painel: o CDK portaliza para fora do DOM do
      shell e o conteúdo perderia o escopo das utilities do anel.
    -->
    <hlm-sheet #sheet [side]="mobile() ? 'bottom' : 'right'">
      <hlm-sheet-content
        *hlmSheetPortal
        [class]="
          mobile()
            ? 'admin-portal-scope flex max-h-[85dvh] flex-col overflow-hidden p-0'
            : 'admin-portal-scope flex h-full flex-col overflow-hidden p-0 sm:max-w-[28rem]'
        "
        [showCloseButton]="false"
      >
        @if (mobile()) {
          <!--
            Alça clicável, não decorativa: no mobile o sheet ocupa 85dvh e o
            backdrop que sobra fica atrás da barra do navegador (ou do banner
            "abrir no app"), então clicar fora não é alcançável e não há Esc —
            sem alvo explícito o gerenciador vira uma tela sem saída. O py-3 dá
            área de toque ao redor da barrinha de 4px.
          -->
          <button
            type="button"
            class="mx-auto shrink-0 cursor-pointer border-0 bg-transparent px-6 py-3"
            data-testid="impressora-fechar-alca"
            aria-label="Fechar"
            (click)="fechar()"
          >
            <span class="block h-[4px] w-[40px] rounded-full bg-border"></span>
          </button>
        }
        <hlm-sheet-header
          [class]="mobile() ? 'items-center text-center' : 'shrink-0 border-b border-border'"
        >
          <h3 hlmSheetTitle class="font-display text-lg font-bold">Impressora</h3>
        </hlm-sheet-header>
        <!--
          Absoluto para não mexer no header (que é flex-col e centralizado no
          mobile). Tamanho icon (40px) no mobile pelo alvo de toque; icon-sm no
          desktop, onde o ponteiro é fino. É o caminho do teclado e do leitor de
          tela nas duas apresentações.
        -->
        <button
          type="button"
          hlmBtn
          variant="ghost"
          [size]="mobile() ? 'icon' : 'icon-sm'"
          class="absolute end-3 top-3 z-10"
          data-testid="impressora-fechar"
          aria-label="Fechar"
          (click)="fechar()"
        >
          <ng-icon name="lucideX" />
        </button>
        <div class="min-h-0 flex-1 overflow-y-auto">
          <ng-template [ngTemplateOutlet]="corpo"></ng-template>
        </div>
      </hlm-sheet-content>
    </hlm-sheet>

    <ng-template #corpo>
      <div class="flex flex-col gap-5 px-6 pb-6 pt-4">
        <!-- Status ------------------------------------------------------- -->
        <section class="flex flex-col gap-2">
          <h4 class="m-0 text-[13px] font-bold text-muted-foreground">Status da conexão</h4>
          <div class="flex flex-wrap items-center gap-2">
            <span hlmBadge [status]="conectada() ? 'success' : 'neutral'">
              {{ conectada() ? 'Conectada' : 'Desconectada' }}
            </span>
            @if (transporte(); as via) {
              <span class="text-sm text-muted-foreground">via {{ via }}</span>
            }
          </div>
          @if (status().serialNumber) {
            <p class="m-0 text-sm text-muted-foreground">
              Número de série: {{ status().serialNumber }}
            </p>
          }
        </section>

        <!-- Conexão ------------------------------------------------------ -->
        <section class="flex flex-col gap-2">
          <h4 class="m-0 text-[13px] font-bold text-muted-foreground">Conectar</h4>
          <div class="flex flex-wrap gap-2">
            <!-- Botão escondido, e não desabilitado, quando o navegador não
                 suporta o transporte: um botão morto só gera tentativa. -->
            @if (suportaBluetooth) {
              <button
                type="button"
                hlmBtn
                variant="outline"
                class="flex-1"
                [disabled]="conectada()"
                (click)="conectarBluetooth()"
              >
                <ng-icon name="lucideBluetooth" />
                Bluetooth
              </button>
            }
            @if (suportaSerial) {
              <button
                type="button"
                hlmBtn
                variant="outline"
                class="flex-1"
                [disabled]="conectada()"
                (click)="conectarSerial()"
              >
                <ng-icon name="lucideUsb" />
                USB
              </button>
            }
            @if (!suportaBluetooth && !suportaSerial) {
              <p class="m-0 text-sm text-muted-foreground">
                Este navegador não suporta Bluetooth nem Serial. Use o Chrome ou o Edge no
                computador ou no Android.
              </p>
            }
          </div>
          @if (conectada()) {
            <button
              type="button"
              hlmBtn
              variant="destructive"
              class="w-full"
              (click)="desconectar()"
            >
              <ng-icon name="lucideUnlink" />
              Desconectar
            </button>
          }
        </section>

        <!-- Logs --------------------------------------------------------- -->
        <section class="flex flex-col gap-2">
          <div class="flex items-center justify-between gap-2">
            <h4 class="m-0 text-[13px] font-bold text-muted-foreground">Logs</h4>
            <button
              type="button"
              hlmBtn
              variant="ghost"
              size="sm"
              [disabled]="logs().length === 0"
              (click)="limparLogs()"
            >
              <ng-icon name="lucideTrash2" />
              Limpar
            </button>
          </div>
          <div
            class="max-h-[200px] overflow-y-auto rounded-input border border-border bg-background p-[0.75rem]"
          >
            @for (log of logs(); track $index) {
              <div class="font-mono text-[11px] leading-relaxed text-muted-foreground">
                {{ log }}
              </div>
            } @empty {
              <p class="m-0 text-sm text-muted-foreground">Nenhum log disponível</p>
            }
          </div>
        </section>

        <button type="button" hlmBtn variant="outline" class="w-full" (click)="fechar()">
          Fechar
        </button>
      </div>
    </ng-template>
  `,
})
export class CheckinImpressoraComponent {
  private readonly niimbot = inject(NiimbotPrinterService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly sheet = viewChild.required<HlmSheet>('sheet');

  /**
   * Status escrito por callback de subscription — precisa ser signal, senão o
   * OnPush não repinta quando a impressora conecta sozinha.
   */
  readonly status = signal<NiimbotConnectionStatus>(this.niimbot.getConnectionStatus());

  /** Lido pelo pai para pintar o ícone de impressora no cabeçalho. */
  readonly conectada: Signal<boolean> = computed(() => this.status().connected);

  protected readonly transporte = computed(() => {
    const s = this.status();
    if (!s.connected || !s.transport) return null;
    return s.transport === 'ble' ? 'Bluetooth' : 'Serial';
  });

  protected readonly logs = signal<readonly string[]>([]);

  /**
   * Suporte do navegador — constante durante a vida da aba, então não é signal.
   * `navigator` não declara `bluetooth`/`serial` no lib.dom, daí o cast pontual.
   */
  protected readonly suportaBluetooth: boolean;
  protected readonly suportaSerial: boolean;

  /** Qual overlay abrir. Escrito por listener de media query → signal. */
  /** `protected`, não `private`: o template lê para escolher o lado do sheet. */
  protected readonly mobile = signal(false);

  constructor() {
    const nav = navigator as Navigator & {
      bluetooth?: { requestDevice?: unknown };
      serial?: { requestPort?: unknown };
    };
    this.suportaBluetooth = !!nav.bluetooth?.requestDevice;
    this.suportaSerial = !!nav.serial?.requestPort;

    // <768px = breakpoint `md` do Tailwind, o mesmo que a página usa para
    // trocar tabela por cards.
    const mq = window.matchMedia('(max-width: 767px)');
    this.mobile.set(mq.matches);
    const onChange = (e: MediaQueryListEvent) => this.mobile.set(e.matches);
    mq.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() => mq.removeEventListener('change', onChange));

    this.niimbot.connectionStatus$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((s) => this.status.set(s));

    this.niimbot.logs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((log) => {
      this.logs.update((atuais) => [...atuais, log].slice(-MAX_LOGS));
    });

    if (this.botaoJaFoiClicado()) this.tentarReconexaoAutomatica();
  }

  // ---- API pública ---------------------------------------------------------

  abrir(): void {
    // Estado pode ter mudado com o gerenciador fechado (o serviço é global).
    this.status.set(this.niimbot.getConnectionStatus());
    this.sheet().open();
  }

  fechar(): void {
    this.sheet().close();
  }

  // ---- conexão -------------------------------------------------------------

  protected conectarBluetooth(): void {
    this.conectar('connectBluetooth', 'Bluetooth');
  }

  protected conectarSerial(): void {
    this.conectar('connectSerial', 'Serial');
  }

  private conectar(metodo: 'connectBluetooth' | 'connectSerial', via: string): void {
    this.marcarBotaoClicado();
    dispararLoader();
    // closeLoader() no finalize cobre sucesso e erro de uma vez. É no-op
    // quando o Swal de erro já está na tela (o handler de erro roda antes do
    // finalize), então não dispensa mais o diálogo.
    this.niimbot[metodo]()
      .pipe(finalize(closeLoader))
      .subscribe({
        next: () => this.status.set(this.niimbot.getConnectionStatus()),
        error: (err: unknown) =>
          Swal.fire({
            icon: 'error',
            title: 'Erro ao conectar',
            text: mensagem(err, `Não foi possível conectar à impressora via ${via}`),
          }),
      });
  }

  protected desconectar(): void {
    dispararLoader();
    this.niimbot
      .disconnect()
      .pipe(timeout(PRAZO_DESCONEXAO_MS), finalize(closeLoader))
      .subscribe({
        next: () => this.status.set(this.niimbot.getConnectionStatus()),
        error: (err: unknown) => {
          // Mesmo sem confirmação da lib, para a UI a impressora saiu: manter
          // "Conectada" deixaria o usuário sem botão para tentar de novo.
          this.status.set(this.niimbot.getConnectionStatus());
          Swal.fire({
            icon: 'error',
            title: 'Erro ao desconectar',
            text: mensagem(err, 'Não foi possível desconectar a impressora'),
          });
        },
      });
  }

  protected limparLogs(): void {
    this.logs.set([]);
  }

  // ---- reconexão automática ------------------------------------------------

  private botaoJaFoiClicado(): boolean {
    try {
      return sessionStorage.getItem(CHAVE_BOTAO_CLICADO) === 'true';
    } catch {
      // Storage bloqueado (modo privado/iframe): sem consentimento registrado,
      // não reconecta sozinho.
      return false;
    }
  }

  private marcarBotaoClicado(): void {
    try {
      sessionStorage.setItem(CHAVE_BOTAO_CLICADO, 'true');
    } catch {
      // Sem storage a reconexão automática simplesmente não acontece na
      // próxima visita — a conexão manual segue funcionando.
    }
  }

  /** Silenciosa por design: falhar aqui é o caso normal (impressora desligada). */
  private tentarReconexaoAutomatica(): void {
    const timer = setTimeout(() => {
      this.niimbot.tryAutoReconnect().subscribe({
        next: () => this.status.set(this.niimbot.getConnectionStatus()),
        error: () => undefined,
      });
    }, ATRASO_RECONEXAO_MS);
    this.destroyRef.onDestroy(() => clearTimeout(timer));
  }
}

function mensagem(err: unknown, padrao: string): string {
  const m = (err as { message?: unknown } | null | undefined)?.message;
  return typeof m === 'string' && m.trim() ? m : padrao;
}
